import React, { useState, useEffect } from "react";
import { GH_COLORS } from "../components/Style";
import { apiClient } from "../services/api";

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_system_role: boolean;
}

interface Permission {
  id: number;
  name: string;
  description: string;
  category: string;
}

interface User {
  id: number;
  username: string;
  role: string;
  use_rbac: boolean;
  roles?: Role[]; // RBAC 역할 목록
  created_at: string;
}

interface UserRole {
  user_id: number;
  role_id: number;
  assigned_at: string;
}

export default function PermissionsManagementPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState<"users" | "roles">("users");

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchUserDetails(selectedUserId);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedRoleId) {
      fetchRolePermissions(selectedRoleId);
    }
  }, [selectedRoleId]);

  const fetchRoles = async () => {
    try {
      const response = await apiClient.getAllRoles();
      setRoles(response.roles || []);
    } catch (error) {
      console.error("역할 조회 실패:", error);
      showMessage("역할 조회에 실패했습니다.", "error");
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await apiClient.getAllPermissions();
      setPermissions(response.permissions || []);
    } catch (error) {
      console.error("권한 조회 실패:", error);
      showMessage("권한 조회에 실패했습니다.", "error");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.getUsers();
      const usersList = response || [];
      setUsers(usersList);
      return usersList;
    } catch (error) {
      console.error("사용자 조회 실패:", error);
      showMessage("사용자 조회에 실패했습니다.", "error");
      return [];
    }
  };

  const fetchUserDetails = async (userId: number, updatedUsers?: User[]) => {
    try {
      setLoading(true);
      const usersList = updatedUsers || users;
      const user = usersList.find((u) => u.id === userId);
      setSelectedUser(user || null);

      // 사용자의 역할 목록 설정
      setUserRoles(user?.roles || []);
    } catch (error) {
      console.error("사용자 상세 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async (roleId: number) => {
    try {
      setLoading(true);
      const response = await apiClient.getRolePermissions(roleId);
      setRolePermissions(response.permissions || []);
    } catch (error) {
      console.error("역할 권한 조회 실패:", error);
      showMessage("역할 권한 조회에 실패했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (userId: number, roleId: number) => {
    try {
      setLoading(true);
      await apiClient.assignRoleToUser(userId, roleId);
      showMessage("역할이 부여되었습니다.", "success");
      const updatedUsers = await fetchUsers(); // 사용자 목록 갱신
      await fetchUserDetails(userId, updatedUsers); // 선택된 사용자 정보 갱신
    } catch (error) {
      console.error("역할 부여 실패:", error);
      showMessage("역할 부여에 실패했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async (userId: number, roleId: number) => {
    if (!window.confirm("정말 이 역할을 회수하시겠습니까?")) return;

    try {
      setLoading(true);
      await apiClient.removeRoleFromUser(userId, roleId);
      showMessage("역할이 회수되었습니다.", "success");
      const updatedUsers = await fetchUsers(); // 사용자 목록 갱신
      await fetchUserDetails(userId, updatedUsers); // 선택된 사용자 정보 갱신
    } catch (error) {
      console.error("역할 회수 실패:", error);
      showMessage("역할 회수에 실패했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRBAC = async (userId: number, currentValue: boolean) => {
    const action = currentValue ? "비활성화" : "활성화";
    if (!window.confirm(`RBAC를 ${action}하시겠습니까?`)) return;

    try {
      setLoading(true);
      await apiClient.toggleUserRBAC(userId, !currentValue);
      showMessage(`RBAC ${action}가 완료되었습니다.`, "success");
      fetchUsers(); // 사용자 목록 갱신
      if (selectedUser?.id === userId) {
        fetchUserDetails(userId); // 선택된 사용자 정보 갱신
      }
    } catch (error) {
      console.error(`RBAC ${action} 실패:`, error);
      showMessage(`RBAC ${action}에 실패했습니다.`, "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string, type: "success" | "error") => {
    setMessage(msg);
    setTimeout(() => setMessage(""), 3000);
  };

  const handleAssignPermissionToRole = async (roleId: number, permissionId: number) => {
    try {
      setLoading(true);
      await apiClient.assignPermissionToRole(roleId, permissionId);
      showMessage("권한이 부여되었습니다.", "success");
      await fetchRolePermissions(roleId);
    } catch (error) {
      console.error("권한 부여 실패:", error);
      showMessage("권한 부여에 실패했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePermissionFromRole = async (roleId: number, permissionId: number) => {
    if (!window.confirm("정말 이 권한을 제거하시겠습니까?")) return;

    try {
      setLoading(true);
      await apiClient.removePermissionFromRole(roleId, permissionId);
      showMessage("권한이 제거되었습니다.", "success");
      await fetchRolePermissions(roleId);
    } catch (error) {
      console.error("권한 제거 실패:", error);
      showMessage("권한 제거에 실패했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const groupPermissionsByCategory = () => {
    const grouped: { [key: string]: Permission[] } = {};
    permissions.forEach((perm) => {
      if (!grouped[perm.category]) {
        grouped[perm.category] = [];
      }
      grouped[perm.category].push(perm);
    });
    return grouped;
  };

  const permissionsByCategory = groupPermissionsByCategory();

  return (
    <div style={{ padding: "40px", maxWidth: "1400px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "12px", fontSize: "28px", fontWeight: 600 }}>
        권한 설정
      </h1>
      <p style={{ color: "#666", marginBottom: "32px" }}>
        사용자에게 역할을 부여하고, 역할별 권한을 관리합니다.
      </p>

      {message && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "24px",
            backgroundColor: message.includes("실패") || message.includes("API")
              ? "#fff3cd"
              : "#d4edda",
            color: message.includes("실패") || message.includes("API") ? "#856404" : "#155724",
            border: message.includes("실패") || message.includes("API")
              ? "1px solid #ffeeba"
              : "1px solid #c3e6cb",
          }}
        >
          {message}
        </div>
      )}

      {/* 탭 전환 */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px", borderBottom: "2px solid #e1e4e8" }}>
        <button
          onClick={() => setActiveTab("users")}
          style={{
            padding: "12px 24px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "users" ? `2px solid ${GH_COLORS.btnPrimaryBg}` : "2px solid transparent",
            color: activeTab === "users" ? GH_COLORS.btnPrimaryBg : "#666",
            fontWeight: activeTab === "users" ? 600 : 400,
            fontSize: "16px",
            cursor: "pointer",
            marginBottom: "-2px",
          }}
        >
          사용자별 역할 관리
        </button>
        <button
          onClick={() => setActiveTab("roles")}
          style={{
            padding: "12px 24px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "roles" ? `2px solid ${GH_COLORS.btnPrimaryBg}` : "2px solid transparent",
            color: activeTab === "roles" ? GH_COLORS.btnPrimaryBg : "#666",
            fontWeight: activeTab === "roles" ? 600 : 400,
            fontSize: "16px",
            cursor: "pointer",
            marginBottom: "-2px",
          }}
        >
          역할별 권한 조회
        </button>
      </div>

      {activeTab === "users" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "24px" }}>
          {/* 사용자 목록 */}
          <div
            style={{
              border: "1px solid #e1e4e8",
              borderRadius: "8px",
              padding: "20px",
              backgroundColor: "#fff",
            }}
          >
            <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>
              사용자 목록
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {users.map((user) => (
                <div
                  key={user.id}
                  onClick={() => setSelectedUserId(user.id)}
                  style={{
                    padding: "12px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    backgroundColor:
                      selectedUserId === user.id ? "#f6f8fa" : "transparent",
                    border: selectedUserId === user.id ? "2px solid #0969da" : "2px solid transparent",
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {user.username}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 역할 관리 */}
          <div
            style={{
              border: "1px solid #e1e4e8",
              borderRadius: "8px",
              padding: "20px",
              backgroundColor: "#fff",
            }}
          >
            {selectedUser ? (
              <>
                <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>
                  {selectedUser.username}의 역할 관리
                </h2>

                {/* 현재 역할 */}
                <div style={{ marginBottom: "24px" }}>
                  <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}>
                    현재 부여된 역할
                  </h3>
                  {userRoles.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {userRoles.map((role) => (
                        <div
                          key={role.id}
                          style={{
                            padding: "12px",
                            border: "1px solid #e1e4e8",
                            borderRadius: "6px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{role.display_name}</div>
                            <div style={{ fontSize: "13px", color: "#666" }}>
                              {role.description}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveRole(selectedUser.id, role.id)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              backgroundColor: "#fff",
                              color: "#cf222e",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                              border: "1px solid #e1e4e8",
                            }}
                          >
                            회수
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "#666", fontSize: "14px" }}>
                      부여된 역할이 없습니다.
                    </p>
                  )}
                </div>

                {/* 역할 부여 */}
                <div>
                  <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}>
                    역할 부여
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {roles.map((role) => {
                      const hasRole = userRoles.some((r) => r.id === role.id);
                      return (
                        <div
                          key={role.id}
                          style={{
                            padding: "12px",
                            border: "1px solid #e1e4e8",
                            borderRadius: "6px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: hasRole ? "#f6f8fa" : "#fff",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{role.display_name}</div>
                            <div style={{ fontSize: "13px", color: "#666" }}>
                              {role.description}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAssignRole(selectedUser.id, role.id)}
                            disabled={loading || hasRole}
                            style={{
                              padding: "6px 12px",
                              border: "none",
                              borderRadius: "6px",
                              backgroundColor: hasRole
                                ? "#e1e4e8"
                                : GH_COLORS.btnPrimaryBg,
                              color: "#fff",
                              cursor: loading || hasRole ? "not-allowed" : "pointer",
                              fontSize: "14px",
                            }}
                          >
                            {hasRole ? "부여됨" : "부여"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <p style={{ color: "#666", textAlign: "center", padding: "40px" }}>
                왼쪽에서 사용자를 선택하세요
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === "roles" && (
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "24px" }}>
          {/* 역할 목록 */}
          <div
            style={{
              border: "1px solid #e1e4e8",
              borderRadius: "8px",
              padding: "20px",
              backgroundColor: "#fff",
              height: "fit-content",
            }}
          >
            <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>
              역할 목록
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {roles.map((role) => (
                <div
                  key={role.id}
                  onClick={() => setSelectedRoleId(role.id)}
                  style={{
                    padding: "12px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    backgroundColor:
                      selectedRoleId === role.id ? "#f6f8fa" : "transparent",
                    border: selectedRoleId === role.id ? "2px solid #0969da" : "2px solid transparent",
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                    {role.display_name}
                  </div>
                  <div style={{ fontSize: "13px", color: "#666" }}>
                    {role.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 권한 관리 */}
          <div
            style={{
              border: "1px solid #e1e4e8",
              borderRadius: "8px",
              padding: "20px",
              backgroundColor: "#fff",
            }}
          >
            {selectedRoleId ? (
              <>
                <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>
                  {roles.find((r) => r.id === selectedRoleId)?.display_name} 권한 관리
                </h2>

                {/* 현재 부여된 권한 */}
                <div style={{ marginBottom: "32px" }}>
                  <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}>
                    현재 부여된 권한 ({rolePermissions.length}개)
                  </h3>
                  {rolePermissions.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {rolePermissions.map((perm) => (
                        <div
                          key={perm.id}
                          style={{
                            padding: "12px",
                            border: "1px solid #e1e4e8",
                            borderRadius: "6px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#f6f8fa",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}>
                              {perm.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {perm.description} • {perm.category}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemovePermissionFromRole(selectedRoleId, perm.id)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              backgroundColor: "#fff",
                              color: "#cf222e",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                              border: "1px solid #e1e4e8",
                            }}
                          >
                            제거
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "#666", fontSize: "14px" }}>
                      부여된 권한이 없습니다.
                    </p>
                  )}
                </div>

                {/* 권한 추가 */}
                <div>
                  <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}>
                    권한 추가
                  </h3>
                  {Object.entries(permissionsByCategory).map(([category, perms]) => {
                    const categoryPerms = perms as Permission[];
                    const availablePerms = categoryPerms.filter(
                      (p) => !rolePermissions.some((rp) => rp.id === p.id)
                    );
                    
                    if (availablePerms.length === 0) return null;

                    return (
                      <div key={category} style={{ marginBottom: "20px" }}>
                        <h4
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            marginBottom: "8px",
                            color: "#0969da",
                          }}
                        >
                          {category} ({availablePerms.length}개)
                        </h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {availablePerms.map((perm) => (
                            <div
                              key={perm.id}
                              style={{
                                padding: "12px",
                                border: "1px solid #e1e4e8",
                                borderRadius: "6px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#fff",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}>
                                  {perm.name}
                                </div>
                                <div style={{ fontSize: "12px", color: "#666" }}>
                                  {perm.description}
                                </div>
                              </div>
                              <button
                                onClick={() => handleAssignPermissionToRole(selectedRoleId, perm.id)}
                                disabled={loading}
                                style={{
                                  padding: "6px 12px",
                                  border: "none",
                                  borderRadius: "6px",
                                  backgroundColor: GH_COLORS.btnPrimaryBg,
                                  color: "#fff",
                                  cursor: loading ? "not-allowed" : "pointer",
                                  fontSize: "14px",
                                }}
                              >
                                추가
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p style={{ color: "#666", textAlign: "center", padding: "40px" }}>
                왼쪽에서 역할을 선택하세요
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
